import { MultiDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { isNil } from 'lodash';
import { createSelector } from 'reselect';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { AppState } from 'src/store';

//#region PricingOverTime component selectors

const selectCompanionViewDataState = (state: AppState) => state.pages.assortmentBuild.pricing.viewDataStateCompanion;
const selectMassEditViewDataState = (state: AppState) => state.pages.assortmentBuild.pricing.viewDataStateMassEdit;
const selectOverTimeViewDataState = (state: AppState) => state.pages.assortmentBuild.pricing.viewDataStateOverTime;
const selectWorklistLiveData = (state: AppState) => state.worklist.liveData;
const selectMassEditData = (state: AppState) => state.pages.assortmentBuild.pricing.massEditData?.tree ?? [];

/** Determines if a viewdefn value was configured */
export const selectPricingHasMassEditViewDefn = (_state: AppState, defns: MultiDefnProps) => defns.view.length >= 6;
export const selectPricingHasMassEditModel = (_state: AppState, massEditModel: string | undefined) =>
  !isNil(massEditModel);

interface PricingMassEditInputs {
  defns: MultiDefnProps;
  massEditModel: string | undefined;
}

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectPricingMassEditInputs = (_state: AppState, inputs: PricingMassEditInputs) => {
  const { defns, massEditModel } = inputs;
  return {
    hasMassEditModel: selectPricingHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectPricingHasMassEditViewDefn(_state, defns),
  };
};

/**
 * Retrieve the viewDataStates to be included in the view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectPricingViewDataStates = createSelector(
  selectCompanionViewDataState,
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditViewDefn,
  (viewDataStateCompanion, viewDataStateMassEdit, viewDataStateOverTime, hasMassEdit) => {
    const viewDataStates = hasMassEdit
      ? [viewDataStateCompanion, viewDataStateOverTime, viewDataStateMassEdit]
      : [viewDataStateCompanion, viewDataStateOverTime];
    return viewDataStates;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectPricingMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectPricingMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

//#endregion

//#region TabbedPricingGrid component selectors

export const selectTabbedPricingHasMassEditViewDefn = (_state: AppState, viewDefns: string[]) => viewDefns.length >= 2;

/**
 * Retrieve massEdit data.
 *
 * For legacy reasons when no massEdit data fetch occurs, the worklist data is returned by default.
 * */
export const selectTabbedPricingMassEditData = createSelector(
  selectWorklistLiveData,
  selectMassEditData,
  selectPricingHasMassEditModel,
  (worklistData, massEditData, hasMassEditModel) => {
    return hasMassEditModel ? massEditData : worklistData.tree;
  },
);

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectTabbedPricingMassEditInputs = (
  _state: AppState,
  massEditModel: string | undefined,
  viewDefns: string[],
) => {
  return {
    hasMassEditModel: selectPricingHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectTabbedPricingHasMassEditViewDefn(_state, viewDefns),
  };
};

/**
 * Retrieve tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const massEditLoaded = isDataLoaded(viewDataStateMassEdit);
    const overTimeLoaded = isDataLoaded(viewDataStateOverTime);
    return hasMassEditModel ? massEditLoaded && overTimeLoaded : overTimeLoaded;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectTabbedPricingMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

/**
 * Retrieve the viewDataStates to be included in the tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedPricingViewDataStates = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectPricingHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const viewDataStates = hasMassEditModel ? [viewDataStateOverTime, viewDataStateMassEdit] : [viewDataStateOverTime];
    return viewDataStates;
  },
);

//#endregion
